/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-empty */
import React, { useEffect } from 'react';
import { Dialog } from 'pcln-design-system';
import { QuickActionsStackVariant } from './chatTypes';
import { DefaultModalHeader } from './components/Header/DefaultModalHeader';
import { ChatMainContentSection } from './ChatContent';
import { ChatbotDataProvider } from './context/ChatbotDataProvider';
import Survey from './components/Survey/Survey';
import { getSpeechOrchestrator } from './Voice/SpeechOrchestrator';
import usePromptRetrieval from './hooks/usePromptRetrieval';
import useLoadPreviousPrompts from './hooks/useLoadPreviousPrompts';
import sanitizeInput from './utils/sanitizeInput';
import ChatMessageErrorBoundary from './components/ErrorBoundary/ChatMessageErrorBoundary';
import { useChatbotState } from './hooks/useChatBotState';
import ErrorComponent from './components/ErrorComponent';
import GoogleMapComponents from './components/Maps/GoogleMapComponents';
import { DialogStyleProvider } from './context/DialogStyleContext';
export const PROMPT_LIMIT = 5;
const DialogContentWrapper = props => {
  const {
    ariaDescription,
    chatContainerHeight,
    isMobile,
    children
  } = props;
  useEffect(() => {
    try {
      const dialog = document.querySelector(`div[aria-describedby="${ariaDescription}"]`);
      if (dialog && isMobile) {
        dialog.style.height = 'calc(100% - 25px)';
        dialog.style.margin = '0px';
        dialog.style.marginTop = '25px';
        dialog.style.borderBottomRightRadius = '0px';
        dialog.style.borderBottomLeftRadius = '0px';
        const dialogContentWrapper = dialog.querySelector('div');
        if (dialogContentWrapper) {
          dialogContentWrapper.style.height = '100%';
          dialogContentWrapper.style.display = 'block';
        }
      }
      if (dialog && !isMobile) {
        dialog.style.height = chatContainerHeight;
        const dialogContentWrapper = dialog.querySelector('div');
        if (dialogContentWrapper) {
          dialogContentWrapper.style.height = '100%';
          dialogContentWrapper.style.display = 'block';
        }
      }
      // UXPT-7760: Fix px-captcha-modal cannot be clicked when above chat-bot modal
      if (dialog) {
        const style = document.createElement('style');
        style.type = 'text/css';
        style.innerHTML = `
          iframe#px-captcha-modal {
            pointer-events: auto;
          }
        `;
        document.head.appendChild(style);
      }
    } catch (err) {}
  }, []);
  return React.createElement(React.Fragment, null, children);
};
/**
 * PclnUcChatBot with Dialog overlay
 * @public
 */
export const PclnUcChatBot = props => {
  const {
    chatContainerHeight = '60%',
    dialogOverlayProps,
    isChatBoxOpened,
    onClose,
    setCachedMessages,
    setCachedPrompts,
    cachedMessages,
    cachedPrompts,
    submitChatMessagesPayload,
    handleMarkerResponse,
    fetchRequestOptions,
    isMobile,
    markerComponents = {},
    discoveryCitySelected,
    onChatDialogRendered,
    logFirstMessageSubmitted,
    logCustomerCarePhoneNumberDisplayed,
    logCustomerCarePhoneNumberClicked,
    quickActionsStackVariant = QuickActionsStackVariant.HORIZONTAL,
    livePersonOptions = {},
    debugMode = false,
    transcriptionFetchRequestOptions,
    speechFetchRequestOptions,
    setiValues = {},
    retrieveUrl,
    isDev = false,
    shouldDisplayGenHotelFeature = true
  } = props;
  const {
    chatMessages,
    prompts,
    isLoading,
    setChatMessages,
    setPrompts,
    setLoadingStatus,
    setRetrievalLoading,
    showSurvey,
    setShowSurvey,
    openErrorComponent,
    setOpenErrorComponent,
    santizedSubmitChatMessagesPayload,
    audioPlayerRef,
    speechOrchestratorRef,
    retrievalLoading,
    setSanitizedSubmitChatMessagesPayload,
    loadMoreMessages,
    setLoadMoreMessages,
    lastPromptId,
    setLastPromptId,
    isMapOpened,
    setOpenedMap
  } = useChatbotState(props);
  const {
    isSessionActive = false
  } = submitChatMessagesPayload;
  // New Speech Stuff for UXPT-6916
  const enableVoice = setiValues === null || setiValues === void 0 ? void 0 : setiValues.enableVoice;
  const enableVoiceTooltip = setiValues === null || setiValues === void 0 ? void 0 : setiValues.enableVoiceTooltip;
  useEffect(() => {
    if (submitChatMessagesPayload.pushPrompt) {
      setSanitizedSubmitChatMessagesPayload(Object.assign(Object.assign({}, submitChatMessagesPayload), {
        pushPrompt: sanitizeInput(submitChatMessagesPayload.pushPrompt)
      }));
    } else {
      setSanitizedSubmitChatMessagesPayload(submitChatMessagesPayload);
    }
  }, [submitChatMessagesPayload]);
  useEffect(() => {
    if (setiValues === null || setiValues === void 0 ? void 0 : setiValues.enableVoice) {
      speechOrchestratorRef.current = getSpeechOrchestrator((speechFetchRequestOptions === null || speechFetchRequestOptions === void 0 ? void 0 : speechFetchRequestOptions.url) || '/genai-svc/genai/speech', (speechFetchRequestOptions === null || speechFetchRequestOptions === void 0 ? void 0 : speechFetchRequestOptions.timeout) || 10000, audioPlayerRef.current, debugMode);
      return () => {
        speechOrchestratorRef.current = null;
      };
    }
  }, []);
  // End of new speech stuff for UXPT-6916
  const chatbotDataContextValue = {
    chatContainerHeight,
    setCachedMessages,
    setCachedPrompts,
    cachedMessages,
    cachedPrompts,
    submitChatMessagesPayload: santizedSubmitChatMessagesPayload,
    handleMarkerResponse,
    fetchRequestOptions,
    markerComponents,
    logFirstMessageSubmitted,
    logCustomerCarePhoneNumberDisplayed,
    logCustomerCarePhoneNumberClicked,
    quickActionsStackVariant,
    isMobile,
    livePersonOptions,
    debugMode,
    setiValues,
    enableVoice,
    enableVoiceTooltip,
    discoveryCitySelected,
    chatMessages,
    loadMoreMessages,
    setChatMessages,
    setLoadMoreMessages,
    prompts,
    setPrompts,
    isLoading,
    setLoadingStatus,
    setOpenErrorComponent,
    openErrorComponent,
    speechFetchRequestOptions,
    transcriptionFetchRequestOptions,
    audioPlayerRef,
    isDev,
    isSessionActive,
    shouldDisplayGenHotelFeature,
    isMapOpened,
    setOpenedMap
  };
  const requestFrom = fetchRequestOptions.headers['x-request-from'];
  const isSurveyEligible = ['travel-itinerary', 'homePage'].includes(requestFrom);
  useEffect(() => {
    if (isChatBoxOpened) {
      onChatDialogRendered();
    }
  }, [isChatBoxOpened]);
  const {
    cguid,
    productId
  } = submitChatMessagesPayload;
  usePromptRetrieval({
    retrieveUrl,
    cguid,
    lastPromptId,
    setCachedMessages,
    setCachedPrompts,
    setChatMessages,
    setPrompts,
    setRetrievalLoading,
    setLastPromptId,
    limit: PROMPT_LIMIT,
    isSessionActive,
    productId,
    requestFrom
  });
  useLoadPreviousPrompts({
    retrieveUrl,
    cguid,
    lastPromptId,
    limit: PROMPT_LIMIT,
    loadMoreMessages,
    setCachedMessages,
    setCachedPrompts,
    setChatMessages,
    setPrompts,
    setRetrievalLoading,
    setLastPromptId,
    setLoadMoreMessages,
    isSessionActive
  });
  return React.createElement(ChatbotDataProvider, Object.assign({}, chatbotDataContextValue), React.createElement(DialogStyleProvider, null, React.createElement("audio", {
    id: 'penny-audio-player',
    ref: audioPlayerRef
  }, React.createElement("track", {
    kind: 'captions'
  })), React.createElement(Dialog, Object.assign({
    showCloseButton: false,
    scrimDismiss: true,
    open: isChatBoxOpened,
    size: 'lg',
    onOpenChange: function (isOpen) {
      if (!isOpen) {
        onClose();
      }
    },
    borderRadius: isMobile ? 'lg' : '2xl',
    shouldDisableTabIndex: true
  }, dialogOverlayProps), React.createElement(DialogContentWrapper, Object.assign({}, dialogOverlayProps, {
    isMobile: isMobile,
    chatContainerHeight: chatContainerHeight
  }), React.createElement(DefaultModalHeader, {
    onClose: function () {
      if (isSurveyEligible && !showSurvey && (setiValues === null || setiValues === void 0 ? void 0 : setiValues.shouldDisplaySurvey)) {
        setShowSurvey(true);
      } else {
        setShowSurvey(false);
        onClose();
      }
    },
    speechEnabled: setiValues === null || setiValues === void 0 ? void 0 : setiValues.enableVoice
  }), !isMapOpened ?
  // display Chat Component
  React.createElement(ChatMessageErrorBoundary, {
    FallbackComponent: React.createElement(ErrorComponent, null)
  }, showSurvey && (setiValues === null || setiValues === void 0 ? void 0 : setiValues.shouldDisplaySurvey) ? React.createElement(Survey, {
    onClose: function () {
      setShowSurvey(false);
      onClose();
    },
    requestFrom: requestFrom
  }) : React.createElement(ChatMainContentSection, {
    retrievalLoading: retrievalLoading
  })) :
  // display Maps Component
  React.createElement(ChatMessageErrorBoundary, {
    FallbackComponent: React.createElement(ErrorComponent, null)
  }, React.createElement(GoogleMapComponents, null))))));
};
PclnUcChatBot.displayName = 'PennyChatBot';